import { useEffect, useRef, useState, useCallback } from "react";
import { gsap } from "gsap";
import { CARD_H, CARD_W, DESIGN_H, DESIGN_W, POS, SLIDES, TOTAL_SLIDES } from "./constant";
import { DesktopControls, SlideImage, TitleDescription } from "./reusableComponent";

const VIS = POS.length;


const HistoryDesktop = () => {
    const stageRef = useRef(null);
    const cardRefs = useRef([]);
    const titleRef = useRef(null);
    const descRef = useRef(null);
    const orderRef = useRef(SLIDES.map((_, i) => i));
    const curRef = useRef(0);

    const [cur, setCur] = useState(0);
    const [busy, setBusy] = useState(false);
    // Calculate the position of the card based on the position index
    const getCardProps = useCallback((posIdx) => {
        const p = POS[posIdx];
        const sw = stageRef.current?.offsetWidth || 1440;
        const sh = stageRef.current?.offsetHeight || 700;
        const sx = sw / DESIGN_W;
        const sy = sh / DESIGN_H;
        const renderedScale = (p.width / CARD_W) * sx;
        const unscaledLeft = p.left * sx - (1 - renderedScale) * CARD_W;
        const unscaledTop = p.top * sy - (1 - renderedScale) * CARD_H;
        return {
            left: unscaledLeft,
            top: unscaledTop,
            scale: renderedScale,
            opacity: p.op,
            filter: `blur(${p.bl}px)`,
            zIndex: VIS - posIdx,
        };
    }, []);
    // Tell cards which seat to go to and move them there
    const renderCards = useCallback((instant) => {
        orderRef.current.forEach((slideIdx, posIdx) => {
            const el = cardRefs.current[slideIdx];
            if (!el) return;

            if (posIdx < VIS) {
                el.style.display = "block";
                const props = getCardProps(posIdx);
                instant
                    ? gsap.set(el, props)
                    : gsap.to(el, { ...props, duration: 0.65, ease: "power3.out", overwrite: true });
            } else {
                gsap.set(el, { display: "none" });
            }
        });
    }, [getCardProps]);

    const navigate = useCallback((dir) => {
        if (busy) return;
        const nextCur = (curRef.current + dir + TOTAL_SLIDES) % TOTAL_SLIDES;

        setBusy(true);
        gsap.to([titleRef.current, descRef.current], {
            opacity: 0, y: dir * -10, duration: 0.22, ease: "power2.in",
            onComplete: () => {
                if (dir === 1) orderRef.current.push(orderRef.current.shift());
                else orderRef.current.unshift(orderRef.current.pop());

                curRef.current = nextCur;
                setCur(nextCur);
                renderCards(false);

                gsap.fromTo(
                    [titleRef.current, descRef.current],
                    { opacity: 0, y: dir * 12 },
                    { opacity: 1, y: 0, duration: 0.32, ease: "power2.out", onComplete: () => setBusy(false) }
                );
            },
        });
    }, [busy, renderCards]);

    useEffect(() => {
        const raf = requestAnimationFrame(() => {
            renderCards(true);

            const sw = stageRef.current?.offsetWidth || 1440;
            orderRef.current.forEach((slideIdx, posIdx) => {
                if (posIdx >= VIS) return;
                const el = cardRefs.current[slideIdx];
                if (!el) return;
                const baseScale = (POS[posIdx].width / CARD_W) * (sw / DESIGN_W);
                gsap.fromTo(el,
                    { opacity: 0, scale: baseScale * 0.82 },
                    { opacity: POS[posIdx].op, scale: baseScale, duration: 0.8, delay: posIdx * 0.07, ease: "power3.out" }
                );
            });

            gsap.fromTo(
                [titleRef.current, descRef.current],
                { opacity: 0, y: 18 },
                { opacity: 1, y: 0, duration: 0.55, delay: 0.25, stagger: 0.09, ease: "power2.out" }
            );
        });

        const handleResize = () => renderCards(true);
        window.addEventListener("resize", handleResize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", handleResize);
        };
    }, [renderCards]);

    return (
        <div ref={stageRef} className="historyDesktopStage">
            {SLIDES.map((slide, i) => (
                <div key={i} ref={(el) => (cardRefs.current[i] = el)} className="historyDesktopCard">
                    <SlideImage src={slide.imgSrc} alt={slide.imgAlt} className="historyCardImg" />
                    <span className="historyDesktopYear">{slide.year}</span>
                </div>
            ))}

            <div className="historyDesktopText">
                <TitleDescription
                    title={SLIDES[cur].title}
                    description={SLIDES[cur].description}
                    titleRef={titleRef}
                    descRef={descRef}
                    titleClassName="historyDesktopTitle"
                    descClassName="historyDesktopDesc"
                />
                <DesktopControls busy={busy} onPrev={() => navigate(-1)} onNext={() => navigate(1)} />
            </div>
        </div>
    );
};

export default HistoryDesktop;